/**
 * NightLytes Drawer Functionality
 * Reusable drawer component for Night Lytes PDP sections
 * 
 * Usage: Initialize with section element
 * const drawer = new NightLytesDrawer(sectionElement);
 */

(function() {
  'use strict';

  class NightLytesDrawer {
    constructor(section) {
      this.section = section;
      this.openDrawers = new Set();
      this.init();
    }

    init() {
      if (!this.section) return;

      // Drawer triggers
      this.section.querySelectorAll('[data-drawer-trigger]').forEach(trigger => {
        this.setupTrigger(trigger);
      });

      // Close buttons
      this.section.querySelectorAll('[data-drawer-close]').forEach(closeBtn => {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const drawerId = closeBtn.getAttribute('data-drawer-close');
          this.closeDrawer(drawerId);
        });
      });

      // Overlay click to close
      this.section.querySelectorAll('[data-drawer-overlay]').forEach(overlay => {
        overlay.addEventListener('click', () => {
          const drawerId = overlay.getAttribute('data-drawer-overlay');
          this.closeDrawer(drawerId);
        });
      });

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openDrawers.size > 0) {
          // Close the most recently opened drawer
          const lastDrawerId = Array.from(this.openDrawers).pop();
          this.closeDrawer(lastDrawerId);
        }
      });
    }

    setupTrigger(trigger) {
      const openDrawer = () => {
        const drawerId = trigger.getAttribute('data-drawer-trigger');
        this.openDrawer(drawerId, trigger);
      };

      trigger.addEventListener('click', openDrawer);
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDrawer();
        }
      });
    }

    openDrawer(drawerId, trigger) {
      try {
        const drawer = document.getElementById(drawerId);
        const overlayId = 'drawer-overlay-' + drawerId.replace('drawer-', '');
        const overlay = document.getElementById(overlayId);

        if (!drawer || !overlay) {
          console.warn('NightLytes Drawer: Drawer or overlay not found', { drawerId, overlayId });
          return;
        }

        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        this.openDrawers.add(drawerId);

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'true');
        }

        // Focus first focusable element
        const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable) {
          focusable.focus();
        }

        // Dispatch custom event
        drawer.dispatchEvent(new CustomEvent('nightlytes:drawer:open', { detail: { drawerId } }));
      } catch (error) {
        console.error('NightLytes Drawer: Error opening drawer', error);
      }
    }

    closeDrawer(drawerId) {
      try {
        const drawer = document.getElementById(drawerId);
        const overlayId = 'drawer-overlay-' + drawerId.replace('drawer-', '');
        const overlay = document.getElementById(overlayId);
        const trigger = this.section.querySelector('[data-drawer-trigger="' + drawerId + '"]');

        if (drawer && overlay) {
          drawer.classList.remove('is-open');
          overlay.classList.remove('is-open');
          document.body.style.overflow = '';
          this.openDrawers.delete(drawerId);

          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }

          // Dispatch custom event
          drawer.dispatchEvent(new CustomEvent('nightlytes:drawer:close', { detail: { drawerId } }));
        }
      } catch (error) {
        console.error('NightLytes Drawer: Error closing drawer', error);
      }
    }

    destroy() {
      // Clean up event listeners if needed
      this.openDrawers.clear();
    }
  }

  // Auto-initialize drawers in Night Lytes sections
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('.nightlytes-section').forEach(section => {
      if (section.querySelector('[data-drawer-trigger]')) {
        new NightLytesDrawer(section);
      }
    });
  });

  // Export for manual initialization if needed
  if (typeof window !== 'undefined') {
    window.NightLytesDrawer = NightLytesDrawer;
  }
})();

